import React, { useState, useEffect } from 'react';
import SimpleMDE from 'react-simplemde-editor';
import ReactMarkdown from 'react-markdown';
import { marked } from 'marked';
import insane from 'insane';
import 'easymde/dist/easymde.min.css';
import Helper from "../helpers";
import config from "../config";

const TermsAndConditionsEditor = ({ venueId }) => {
    const [markdownContent, setMarkdownContent] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    
    // Fetch existing T&Cs when component loads
    useEffect(() => {
        const fetchTerms = async () => {
            try {
                const token = await Helper.getCookie('wh-jwt');
                const response = await fetch(`${config.apiBaseUrl}/api/venue/get/${venueId}/terms`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setMarkdownContent(data.termsContent || '');
                } else {
                    console.error('Failed to fetch terms and conditions');
                }
            } catch (err) {
                console.error('Error fetching terms:', err);
            }
        };
        
        if (venueId) {
            fetchTerms();
        }
    }, [venueId]);
    
    const handleSave = async () => {
        try {
            setIsSaving(true);
            setError('');
            setSuccessMessage('');
            
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${config.apiBaseUrl}/api/venue/set/${venueId}/terms`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ termsContent: markdownContent })
            });
            
            if (response.ok) {
                setIsEditing(false);
                setSuccessMessage('Terms and conditions saved successfully!');
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError('Failed to save terms and conditions');
            }
        } catch (err) {
            setError('An error occurred while saving');
            console.error('Error saving terms:', err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-display font-bold text-luxury-pearl">
                    {isEditing ? 'Edit Terms & Conditions' : 'Terms & Conditions'}
                </h2>
                {!isEditing ? (
                    <button 
                        onClick={() => setIsEditing(true)}
                        className="btn-primary px-6 py-2"
                    >
                        Edit Terms
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button 
                            onClick={() => setIsEditing(false)}
                            disabled={isSaving}
                            className="border border-luxury-graphite hover:border-luxury-stone bg-luxury-obsidian hover:bg-luxury-slate text-luxury-pearl px-6 py-2 rounded-lg transition-all duration-200 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleSave}
                            disabled={isSaving}
                            className="btn-primary px-6 py-2 disabled:opacity-50"
                        >
                            {isSaving ? 'Saving...' : 'Save Terms'}
                        </button>
                    </div>
                )}
            </div>
            
            {error && (
                <div className="p-4 bg-red-600/10 border border-red-600/30 rounded-lg">
                    <p className="text-red-400">{error}</p>
                </div>
            )}
            
            {successMessage && (
                <div className="p-4 bg-emerald-600/10 border border-emerald-600/30 rounded-lg">
                    <p className="text-emerald-400">{successMessage}</p>
                </div>
            )}
            
            {isEditing ? (
                <div className="bg-luxury-obsidian border border-luxury-graphite/30 rounded-lg p-4">
                    <SimpleMDE
                        value={markdownContent}
                        onChange={setMarkdownContent}
                        options={{
                            spellChecker: false,
                            placeholder: "Write your venue's terms and conditions here using Markdown formatting...",
                            status: ['lines', 'words'],
                            toolbar: [
                                'bold', 'italic', 'heading', '|',
                                'quote', 'unordered-list', 'ordered-list', '|',
                                'link', '|',
                                'preview', 'side-by-side', 'fullscreen', '|',
                                'guide'
                            ],
                            previewRender: (plainText) => {
                                // Use a markdown parser to convert to HTML
                                let clean = insane(plainText);
                                return marked.parse(clean);
                            }
                        }}
                    />
                </div>
            ) : (
                <div className="bg-luxury-obsidian border border-luxury-graphite/30 rounded-lg p-6 min-h-[300px]">
                    {markdownContent ? (
                        <div className="prose prose-invert prose-emerald max-w-none">
                            <ReactMarkdown>
                                {markdownContent}
                            </ReactMarkdown>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-center py-12">
                            <p className="text-luxury-pearl/40 text-lg font-medium mb-2">
                                No terms and conditions have been added yet
                            </p>
                            <p className="text-luxury-pearl/30 text-sm">
                                Click "Edit Terms" to add your venue's terms and conditions
                            </p>
                        </div>
                    )}
                </div>
            )}
            
            {!isEditing && markdownContent && (
                <div className="p-4 bg-blue-600/10 border border-blue-600/30 rounded-lg">
                    <p className="text-blue-300 text-sm">
                        💡 <strong>Tip:</strong> These terms will be displayed to users during the signup process for your venue.
                    </p>
                </div>
            )}
        </div>
    );
};

export default TermsAndConditionsEditor;